import maplibregl from 'maplibre-gl';

class MMapAbstract {
  zoom = 12;

  isLoaded = false;

  constructor(
    parentElement,
    zoom,
    center,
    clickableLayers = ['stops', 'natural-point-label', 'poi-label'],
    cooperativeGestures = false,
  ) {
    const mapZoom = zoom ?? this.zoom;

    this.center = center ?? JSON.parse(parentElement.dataset.center);
    this.zoom = zoom;
    this.parentElement = parentElement;
    this.element = document.createElement('div');
    this.element.classList.add('m-map');

    this.stopElement = document.createElement('svg');
    this.stopElement.setAttribute('width', '14px');
    this.stopElement.setAttribute('height', '14px');
    this.stopElement.innerHTML = `
      <circle fill="black" cx="7" cy="7" r="7" class=""></circle>
      <circle fill="white" cx="7" cy="7" r="4" class=""></circle>
    `;

    this.clickableLayers = clickableLayers;
    this.popupMaxWidth = null;
    this.boundsPadding = null;
    this.userInteracted = false;

    // event listener

    // init
    parentElement.appendChild(this.element);

    this.map = new maplibregl.Map({
      container: this.element,
      style: '/mapbox-proxy/style.json',
      center: this.center,
      zoom: mapZoom,
      // needed to get jpg from canvas
      // https://stackoverflow.com/questions/42483449/mapbox-gl-js-export-map-to-png-or-pdf
      preserveDrawingBuffer: true,
      attributionControl: false,
      cooperativeGestures,
      maxBounds: [
        [this.center[0] - 12, this.center[1] - 8],
        [this.center[0] + 12, this.center[1] + 8],
      ], // Set the map's geographical boundaries.
      locale: {
        'AttributionControl.ToggleAttribution': 'Attribution umschalten',
        'AttributionControl.MapFeedback': 'Karten-Feedback',
        'FullscreenControl.Enter': 'Vollbildmodus aktivieren',
        'FullscreenControl.Exit': 'Vollbildmodus verlassen',
        'GeolocateControl.FindMyLocation': 'Meinen Standort anzeigen',
        'GeolocateControl.LocationNotAvailable': 'Standort nicht verfügbar',
        'Map.Title': 'Karte',
        'Marker.Title': 'Kartenmarker',
        'NavigationControl.ResetBearing': 'Nach Norden ausrichten',
        'NavigationControl.ZoomIn': 'Vergrößern',
        'NavigationControl.ZoomOut': 'Verkleinern',
        'Popup.Close': 'Popup schließen',
        'CooperativeGesturesHandler.WindowsHelpText': 'Halten Sie Strg gedrückt, um die Karte zu zoomen.',
        'CooperativeGesturesHandler.MacHelpText': 'Halten Sie ⌘ gedrückt, um die Karte zu zoomen',
        'CooperativeGesturesHandler.MobileHelpText': 'Verwenden Sie zwei Finger, um die Karte zu bewegen.',
      },
    });
    this.map.addControl(new maplibregl.AttributionControl({
      compact: true,
    }));
    this.map.addControl(new maplibregl.NavigationControl());
    this.map.addControl(new maplibregl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
      showUserHeading: true,
    }));

    // event listener
    this.map.on('load', () => {
      this.isLoaded = true;
    });

    // Change the cursor when the mouse is clickable layers
    this.clickableLayers.forEach((layerId) => {
      this.map.on('mouseenter', layerId, this.onMouseenter.bind(this));
      this.map.on('mouseleave', layerId, this.onMouseleave.bind(this));
    });
    this.map.on('click', this.onClick.bind(this));
    this.map.on('contextmenu', (event) => {
      const bbox = [
        [event.point.x - 10, event.point.y - 10],
        [event.point.x + 10, event.point.y + 10],
      ];
      const selectedFeature = this.map.queryRenderedFeatures(bbox)?.[0];

      const name = selectedFeature?.properties?.name ?? 'Ausgewählter Ort';
      const coordinates = event.lngLat;
      const popupContentElement = Object.assign(document.createElement('div'), {
        className: 'm-popup-content',
        innerHTML: `
          <h4 class="a-heading">${name}</h4>
          <div class="a-text -teaser">
            <div class="a-text__content">
              <p><button class="a-button" data-action="set-from">Als Start übernehmen</button></p>
              <p><button class="a-button" data-action="set-to">Als Ziel übernehmen</button></p>
              <!--<small>${selectedFeature?.properties?.id}</small>-->
            </div>
          </div>
        `,
      });
      popupContentElement.dataset.geoPosition = `${coordinates.lng},${coordinates.lat}`;
      popupContentElement.dataset.name = name;
      popupContentElement.querySelectorAll('button').forEach((buttonElement) => {
        buttonElement.addEventListener('click', this.onPopupButtonClick.bind(this));
      });
      this.showPopup(coordinates, popupContentElement);

      /* eslint-disable-next-line no-underscore-dangle */
      window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'Rightclick', 'Popup']);
    });

    window.addEventListener('resize', this.onResize.bind(this));

    this.onResize();
  }

  onResize() {
    if (window.matchMedia('(min-width: 920px)').matches) {
      this.popupMaxWidth = '420px';
      this.boundsPadding = {
        top: 40,
        bottom: 40,
        left: 40,
        right: 40,
      };
    } else {
      this.popupMaxWidth = '240px';
      this.boundsPadding = {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20,
      };
    }
  }

  onMouseenter() {
    this.map.getCanvas().style.cursor = 'pointer';
  }

  onMouseleave() {
    this.map.getCanvas().style.cursor = '';
  }

  onClick(event) {
    // Set `bbox` as 5px reactangle area around clicked point.
    const bbox = [
      [event.point.x - 2, event.point.y - 2],
      [event.point.x + 2, event.point.y + 2],
    ];
    // Find features intersecting the bounding box.
    const selectedFeature = this.map.queryRenderedFeatures(bbox, {
      layers: this.clickableLayers,
    })?.[0];
    if (selectedFeature) {
      const popupContentElement = Object.assign(document.createElement('div'), {
        className: 'm-popup-content',
        innerHTML: `
          <h4 class="a-heading">${selectedFeature.properties.name}</h4>
          <div class="a-text -teaser">
            <div class="a-text__content">
              <p><button class="a-button" data-action="set-from">Als Start übernehmen</button></p>
              <p><button class="a-button" data-action="set-to">Als Ziel übernehmen</button></p>
              <!--<small>${selectedFeature.properties.id}</small>-->
            </div>
          </div>
        `,
      });
      if (selectedFeature.layer.id === 'stops') {
        popupContentElement.dataset.stopId = selectedFeature.properties.id;
      } else {
        const coordinates = event.lngLat;
        popupContentElement.dataset.geoPosition = `${coordinates.lng},${coordinates.lat}`;
      }
      popupContentElement.dataset.name = selectedFeature.properties.name;
      popupContentElement.querySelectorAll('button').forEach((buttonElement) => {
        buttonElement.addEventListener('click', this.onPopupButtonClick.bind(this));
      });
      this.showPopup(selectedFeature.geometry.coordinates, popupContentElement);

      /* eslint-disable-next-line no-underscore-dangle */
      window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'Click', 'Popup']);
    }
  }

  onPopupButtonClick(event) {
    console.log(event);
  }

  showPopup(coordinates, html) {
    new maplibregl.Popup({
      offset: 8,
      maxWidth: this.popupMaxWidth,
    })
      .setLngLat(coordinates)
      .setDOMContent(html)
      .addTo(this.map);
  }

  zoomToStation(coordinates) {
    this.userInteracted = true;
    this.map.flyTo({ center: coordinates, zoom: 18 });
  }

  fitCoordinates(coordinates, duration = null) {
    const { map } = this;
    // Create a 'LngLatBounds' with both corners at the first coordinate.
    const bounds = new maplibregl.LngLatBounds(
      coordinates[0],
      coordinates[1],
    );

    // Extend the 'LngLatBounds' to include every coordinate in the bounds result.
    coordinates.forEach((coord) => {
      bounds.extend(coord);
    });

    if (duration !== null) {
      map.fitBounds(bounds, {
        padding: this.boundsPadding,
        duration,
      });
    } else {
      map.fitBounds(bounds, {
        padding: this.boundsPadding,
      });
    }
  }

  removeRoute(id = '') {
    const { map } = this;
    if (map.getLayer(id)) {
      map.removeLayer(id);
    }
    if (map.getSource(id)) {
      map.removeSource(id);
    }
  }

  addLine(
    id = '',
    coordinates = [],
    color = '#000',
    mode = '',
  ) {
    if (coordinates) {
      const { map } = this;

      if (!map.getSource(id)) {
        map.addSource(id, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {
              color,
            },
            geometry: {
              type: 'LineString',
              coordinates,
            },
          },
        });

        const paint = {
          'line-color': {
            type: 'identity',
            property: 'color',
          },
          'line-width': 3,
        };

        if (mode === 'walk') {
          paint['line-dasharray'] = [1 * 2, 0.6 * 2];
        }

        map.addLayer({
          id,
          type: 'line',
          source: id,
          layout: {
            'line-join': 'round',
          },
          paint,
        }, 'road-label-simple');
      }
    }
  }

  addPoint(
    name,
    coordinates,
    color = 'black',
  ) {
    if (coordinates) {
      const { map } = this;
      const id = `${name}-${coordinates.toString()}`;

      if (!map.getSource(id)) {
        map.addSource(id, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates,
            },
            properties: {
              title: name,
            },
          },
        });

        const paint = {
          'circle-radius': 5,
          'circle-stroke-color': color,
          'circle-stroke-width': 3,
          'circle-color': 'white',
        };

        map.addLayer({
          id,
          type: 'circle',
          source: id,
          paint,
        });
      }
    }
  }

  removePoint(name, coordinates) {
    if (coordinates) {
      const { map } = this;
      const id = `${name}-${coordinates.toString()}`;
      if (map.getLayer(id)) {
        map.removeLayer(id);
        map.removeSource(id);
      }
    }
  }

  generateImg() {
    this.element.querySelector('.m-map__img')?.remove();
    this.element.appendChild(Object.assign(document.createElement('img'), {
      className: 'm-map__img',
      src: this.element.querySelector('canvas').toDataURL('image/jpg'),
    }));
  }
}

export default MMapAbstract;
